"use client";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Script from "next/script";
import { BoltIcon, ShieldCheckIcon, Squares2X2Icon, UserGroupIcon } from "@heroicons/react/24/solid";
import AppImage from "@/components/ui/AppImage";
import { useHomeLanguage } from "./HomeLanguageContext";

const REGISTER_ENDPOINT = "https://demo.kolabo.id/api/company-register";
const REGISTER_SUCCESS_MESSAGE = "Data created successfully. Please verify your email.";
const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || "0x4AAAAAABsBNHaZIB_hjsLt";

const trustedClients = [
  { src: "/assets/optimized/client1-a6146b171c.webp", name: "Client Kolabo 1" },
  { src: "/assets/optimized/client2-a88f7d916a.webp", name: "Client Kolabo 2" },
  { src: "/assets/optimized/client03-29c28f1c22.webp", name: "Client Kolabo 3" },
  { src: "/assets/optimized/client04-c56d5269c8.webp", name: "Client Kolabo 4" },
];

const moduleOptions = ["HRM", "Accounting", "CRM", "Project Management", "POS", "Custom"] as const;

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement | string,
        options: {
          sitekey: string;
          theme?: "light" | "dark" | "auto";
          callback?: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
        }
      ) => string | number;
      reset: (widgetId?: string | number) => void;
    };
    fbq?: (...args: unknown[]) => void;
    ttq?: { track: (eventName: string, data?: Record<string, unknown>) => void };
    gtag?: (...args: unknown[]) => void;
  }
}

export default function TrialForm() {
  const { lang } = useHomeLanguage();
  const [form, setForm] = useState({
    namaLengkap: "",
    namaPerusahaan: "",
    email: "",
    whatsapp: "",
  });
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileLoaded, setTurnstileLoaded] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const formCardRef = useRef<HTMLDivElement>(null);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetIdRef = useRef<string | number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("revealed");
        });
      },
      { threshold: 0.08 }
    );
    sectionRef.current?.querySelectorAll(".animate-on-scroll").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const scrollToRegisterSection = (behavior: ScrollBehavior) => {
      const hash = window.location.hash.toLowerCase();
      if (hash === "#daftar") {
        // On mobile, land directly on the registration form card.
        // On desktop, keep section-level landing so context stays visible.
        const isMobile = window.matchMedia("(max-width: 1023px)").matches;
        const target = isMobile ? formCardRef.current : sectionRef.current;
        target?.scrollIntoView({ behavior, block: "start" });
        return;
      }
      if (hash === "#trial") {
        sectionRef.current?.scrollIntoView({ behavior, block: "start" });
      }
    };

    // Support direct open with /#daftar or /#trial.
    scrollToRegisterSection("auto");

    const handleHashChange = () => scrollToRegisterSection("smooth");
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const renderTurnstile = useCallback(() => {
    if (!turnstileLoaded || !window.turnstile || !turnstileRef.current || turnstileWidgetIdRef.current !== null) {
      return;
    }

    turnstileWidgetIdRef.current = window.turnstile.render(turnstileRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      theme: "light",
      callback: (token) => {
        setTurnstileToken(token);
        setErrorMessage("");
      },
      "expired-callback": () => {
        setTurnstileToken("");
      },
      "error-callback": () => {
        setTurnstileToken("");
      },
    });
  }, [turnstileLoaded]);

  useEffect(() => {
    renderTurnstile();
  }, [renderTurnstile]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (errorMessage) setErrorMessage("");
  };

  const validateForm = () => {
    if (!form.namaPerusahaan.trim()) return lang === "id" ? "Nama perusahaan wajib diisi." : "Company name is required.";
    if (!form.email.trim()) return lang === "id" ? "Email wajib diisi." : "Email is required.";
    if (!form.namaLengkap.trim()) return lang === "id" ? "Nama PIC wajib diisi." : "PIC name is required.";
    if (!form.whatsapp.trim()) return lang === "id" ? "Nomor telepon wajib diisi." : "Phone number is required.";
    if (!turnstileToken) return lang === "id" ? "Captcha diperlukan. Mohon centang/verifikasi dulu." : "Captcha is required. Please complete verification first.";
    return "";
  };

  const toggleModule = (module: string) => {
    setSelectedModules((prev) =>
      prev.includes(module) ? prev.filter((item) => item !== module) : [...prev, module]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");
    let isSuccess = false;

    try {
      const payload = new URLSearchParams();
      payload.append("company", form.namaPerusahaan.trim());
      payload.append("email", form.email.trim());
      payload.append("pic_name", form.namaLengkap.trim());
      payload.append("phone", form.whatsapp.trim());
      payload.append("cf-turnstile-response", turnstileToken);

      // API belum wajib dukung field ini, tapi kita tetap kirim untuk future analytics/use-case routing.
      payload.append("interested_modules", selectedModules.join(","));
      selectedModules.forEach((module) => payload.append("interested_modules[]", module));

      const response = await fetch(REGISTER_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
        },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let responseBody: { message?: string } = {};

      try {
        responseBody = JSON.parse(responseText) as { message?: string };
      } catch {
        responseBody = {};
      }

      const message = responseBody?.message || "";

      if (!response.ok) {
        throw new Error(message || responseText || "Terjadi kesalahan.");
      }

      if (message === "The company format is invalid.") {
        setErrorMessage(
          lang === "id"
            ? 'Error: Nama perusahaan hanya boleh berisi huruf, angka, ".", atau ",".'
            : 'Error: Company name can contain only letters, numbers, ".", or ",".'
        );
        return;
      }

      if (message !== REGISTER_SUCCESS_MESSAGE) {
        setErrorMessage(message || (lang === "id" ? "Terjadi kesalahan saat proses pendaftaran." : "An error occurred during registration."));
        return;
      }

      setSubmitted(true);
      isSuccess = true;

      window.fbq?.("track", "StartTrial");
      window.ttq?.track("CompleteRegistration", {});
      window.gtag?.("event", "conversion", {
        send_to: "AW-16764941595/Yi7nCI-jz-UZEJvqkro-",
        value: 1.0,
        currency: "IDR",
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : lang === "id" ? "Terjadi kesalahan." : "An error occurred.";
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
      if (!isSuccess && window.turnstile && turnstileWidgetIdRef.current !== null) {
        window.turnstile.reset(turnstileWidgetIdRef.current);
        setTurnstileToken("");
      }
    }
  };

  const benefits = [
    {
      Icon: BoltIcon,
      title: lang === "id" ? "Aktivasi cepat" : "Fast activation",
      desc:
        lang === "id"
          ? "Akun siap dipakai dalam hitungan detik setelah registrasi."
          : "Your account is ready to use within seconds after registration.",
    },
    {
      Icon: Squares2X2Icon,
      title: lang === "id" ? "Akses semua modul" : "Access all modules",
      desc: lang === "id" ? "Trial mencakup seluruh fitur utama Kolabo." : "The trial includes all main Kolabo features.",
    },
    {
      Icon: UserGroupIcon,
      title: lang === "id" ? "Dibantu tim Kolabo" : "Assisted by Kolabo team",
      desc:
        lang === "id"
          ? "Tim sales kami siap follow-up untuk onboarding Anda."
          : "Our sales team is ready to assist your onboarding.",
    },
  ];

  return (
    <section
      id="trial"
      className="pt-12 sm:pt-14 lg:pt-16 pb-36 sm:pb-28 lg:pb-28 bg-white relative overflow-hidden scroll-mt-28 lg:scroll-mt-32"
      ref={sectionRef}
    >
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
        strategy="afterInteractive"
        onLoad={() => setTurnstileLoaded(true)}
      />
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 grid-pattern opacity-15" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/6 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[420px] h-[420px] bg-secondary/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-7 sm:gap-8 lg:gap-10 items-start lg:items-center">
          <div className="lg:col-span-5 animate-on-scroll">
            <span className="section-label mb-6 inline-flex">
              <span className="w-1.5 h-1.5 rounded-full bg-cta pulse-dot inline-block" />
              {lang === "id" ? "Trial Gratis 14 Hari" : "14-Day Free Trial"}
            </span>
            <h2 className="font-heading text-[clamp(2rem,8.4vw,2.65rem)] sm:text-4xl lg:text-5xl font-bold text-fg leading-[1.08] tracking-[-0.03em] mb-4 mt-5 break-words">
              {lang === "id" ? (
                <>
                  <span className="block sm:inline">Buat Akun </span>
                  <span className="gradient-text inline-block">Kolabo</span>
                  <span className="block sm:inline"> Sekarang</span>
                </>
              ) : (
                <>
                  <span className="block sm:inline">Create Your </span>
                  <span className="gradient-text inline-block">Kolabo</span>
                  <span className="block sm:inline"> Account</span>
                </>
              )}
            </h2>
            <p className="text-fg-muted leading-relaxed mb-7 text-[17px]">
              {lang === "id"
                ? "Hanya butuh beberapa detik untuk mencoba seluruh fitur Kolabo tanpa kewajiban berlangganan."
                : "It only takes seconds to try all Kolabo features with no subscription obligation."}
            </p>
            <div className="grid sm:grid-cols-2 gap-2 text-[12px] text-fg-muted mb-6">
              <p className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" />{lang === "id" ? "HRM, Payroll, dan approval" : "HRM, Payroll, and approvals"}</p>
              <p className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" />{lang === "id" ? "Accounting dan laporan realtime" : "Accounting and real-time reports"}</p>
              <p className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" />{lang === "id" ? "CRM untuk follow-up penjualan" : "CRM for sales follow-up"}</p>
              <p className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" />{lang === "id" ? "Project board dan task lintas tim" : "Cross-team project board and tasks"}</p>
            </div>

            <div className="space-y-2.5 stagger-children">
              {benefits.map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3.5 sm:gap-4 p-3.5 sm:p-4 rounded-lg bg-white border border-stone-200 hover:border-primary/20 hover:bg-primary/5 transition-all duration-300 group"
                >
                  <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 shrink-0 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <item.Icon className="w-5 h-5 text-primary" aria-hidden="true" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-fg font-semibold text-sm tracking-tight">{item.title}</p>
                    <p className="text-fg-muted text-xs mt-0.5 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-col min-[430px]:flex-row min-[430px]:items-center gap-3 p-4 rounded-lg bg-white border border-stone-200">
              <div className="flex -space-x-2">
                {trustedClients.map((client) => (
                  <div
                    key={client.src}
                    className="w-8 h-8 rounded-full bg-white border-2 border-white shadow-sm overflow-hidden flex items-center justify-center"
                  >
                    <AppImage
                      src={client.src}
                      alt={`Logo ${client.name}`}
                      width={28}
                      height={28}
                      className="w-full h-full object-contain p-1"
                    />
                  </div>
                ))}
              </div>
              <div className="min-w-0">
                <p className="text-fg text-sm font-semibold tracking-tight">
                  {lang === "id" ? "300+ perusahaan bergabung" : "300+ companies joined"}
                </p>
                <p className="text-fg-muted text-xs leading-relaxed">
                  {lang === "id" ? "Mulai trial dan lihat kecocokannya untuk bisnis Anda" : "Start your trial and see the fit for your business"}
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 animate-on-scroll">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/15 to-secondary/10 rounded-2xl blur-2xl scale-105 pointer-events-none" />

              <div
                id="daftar"
                ref={formCardRef}
                className="relative bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 shadow-glow-md overflow-hidden border border-stone-200 scroll-mt-24 sm:scroll-mt-28"
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-brand" />

                {submitted ? (
                  <div className="text-center py-14">
                    <div className="w-20 h-20 rounded-full bg-gradient-brand flex items-center justify-center mx-auto mb-6 shadow-brand-lg float-slow">
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    </div>
                    <h3 className="font-heading text-2xl font-bold text-fg mb-3 tracking-tight">
                      {lang === "id" ? "Pendaftaran Berhasil!" : "Registration Successful!"}
                    </h3>
                    <p className="text-fg-muted max-w-sm mx-auto text-sm leading-relaxed">
                      {lang === "id"
                        ? "Akun berhasil dibuat. Silakan cek email Anda untuk verifikasi dan informasi akun Kolabo."
                        : "Your account has been created successfully. Please check your email for verification and account details."}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="mb-6 sm:mb-7">
                      <h3 className="font-heading text-xl font-bold text-fg mb-1.5 tracking-tight">
                        {lang === "id" ? "Daftar Kolabo" : "Register Kolabo"}
                      </h3>
                      <p className="text-fg-muted text-sm">
                        {lang === "id"
                          ? "Nomor WA dan Email aktif diperlukan untuk verifikasi akun."
                          : "An active WhatsApp number and email are required for account verification."}
                      </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
                      {errorMessage && (
                        <p className="text-red-600 text-sm leading-relaxed bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                          {errorMessage}
                        </p>
                      )}

                      {(
                        lang === "id"
                          ? [
                              { id: "namaLengkap", label: "Nama Lengkap", type: "text", placeholder: "Masukkan nama lengkap Anda" },
                              { id: "namaPerusahaan", label: "Nama Perusahaan / Bisnis", type: "text", placeholder: "Nama perusahaan atau bisnis Anda" },
                              { id: "email", label: "Email Aktif", type: "email", placeholder: "Email perusahaan / pribadi" },
                              { id: "whatsapp", label: "Nomor WhatsApp Aktif", type: "tel", placeholder: "08xxxxxxxxxx" },
                            ]
                          : [
                              { id: "namaLengkap", label: "Full Name", type: "text", placeholder: "Enter your full name" },
                              { id: "namaPerusahaan", label: "Company / Business Name", type: "text", placeholder: "Your company or business name" },
                              { id: "email", label: "Active Email", type: "email", placeholder: "email@company.com" },
                              { id: "whatsapp", label: "Active WhatsApp Number", type: "tel", placeholder: "+62xxxxxxxxxx" },
                            ]
                      ).map((field) => (
                        <div key={field.id}>
                          <label htmlFor={field.id} className="block text-[11px] sm:text-xs font-bold text-fg/50 uppercase tracking-[0.12em] sm:tracking-[0.14em] mb-2">
                            {field.label} <span className="text-red-500">*</span>
                          </label>
                          <input
                            id={field.id}
                            name={field.id}
                            type={field.type}
                            required
                            value={form[field.id as keyof typeof form]}
                            onChange={handleChange}
                            placeholder={field.placeholder}
                            className="form-input w-full px-4 py-2.5 sm:py-3 rounded-lg border border-stone-200 bg-stone-50 text-fg placeholder-stone-400 text-sm transition-all duration-300"
                          />
                        </div>
                      ))}

                      <div className="pb-1">
                        <p className="text-[12px] text-fg-muted mb-3">
                          {lang === "id"
                            ? "Pilih modul yang Anda butuhkan (bisa lebih dari satu)."
                            : "Pick modules that You want (can multiple)."}
                        </p>
                        <div className="flex flex-wrap gap-2.5">
                          {moduleOptions.map((module) => {
                            const active = selectedModules.includes(module);
                            return (
                              <button
                                key={module}
                                type="button"
                                onClick={() => toggleModule(module)}
                                className={`relative inline-flex items-center gap-2 rounded-full border px-3.5 sm:px-4 py-2 text-[13px] sm:text-sm font-semibold transition-all duration-300 ${
                                  active
                                    ? "border-emerald-300 bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md"
                                    : "border-stone-300 bg-white text-fg/80 hover:border-primary/35 hover:text-fg"
                                }`}
                                aria-pressed={active}
                              >
                                <span>{module}</span>
                                {active && (
                                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/20 border border-white/35">
                                    <svg
                                      width="12"
                                      height="12"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2.5"
                                    >
                                      <path d="M20 6L9 17l-5-5" />
                                    </svg>
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="turnstile-wrap">
                        <div ref={turnstileRef} />
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-gradient w-full text-white font-semibold py-3.5 rounded-lg shadow-brand-lg text-[14px] flex items-center justify-center gap-2.5 mt-2 tracking-wide disabled:opacity-70 disabled:cursor-not-allowed disabled:translate-y-0"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                        </svg>
                        {isSubmitting ? "Loading..." : lang === "id" ? "Daftar Kolabo" : "Register Kolabo"}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
